import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPublicPlots, searchPublicPlots } from '../services/api';
import { Helmet } from 'react-helmet';
import PlotCard from '../components/PlotCard';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Pagination from '../components/Pagination';
import NotFound from '../components/NotFound';

const Plots = () => {
  const [plots, setPlots] = useState([]);
  const [filteredPlots, setFilteredPlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Skeleton loader component
  const PlotSkeleton = () => (
    <div className="col-xl-4 col-md-6">
      <div className="project-card style1-1">
        <div className="project-card-details">
          <h4 className="box-title">
            <Skeleton width={200} />
          </h4>
          <Skeleton width={150} height={24} style={{ margin: '10px 0' }} />
          <Skeleton width={120} height={20} style={{ marginBottom: '15px' }} />
          <Skeleton width={100} height={35} style={{ marginTop: "10px" }} />
        </div>
      </div>
    </div>
  );

  const handleSearchChange = async (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Clear any existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Set new timeout
    const timeout = setTimeout(async () => {
      if (!value.trim()) {
        setFilteredPlots(plots);
        return;
      }

      setLoading(true);
      try {
        const response = await searchPublicPlots(value);
        setFilteredPlots(response?.payload?.page || []);
      } catch (error) {
        console.error("Search failed:", error);
        setError(error.message);
        setFilteredPlots([]);
      } finally {
        setLoading(false);
      }
    }, 500); // Wait 500ms after user stops typing

    setTypingTimeout(timeout);
  };

  const getPlots = async (page = 0) => {
    setLoading(true);
    try {
      const response = await fetchPublicPlots(page);
      if (response?.payload) {
        setPlots(response.payload.page);
        setFilteredPlots(response.payload.page);
        setTotalPages(response.payload.totalPages);
        setCurrentPage(response.payload.currentPage);
      }
    } catch (error) {
      console.error("Failed to fetch plots:", error);
      setError(error.message);
      setPlots([]);
      setFilteredPlots([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPlots();
    return () => {
      // Cleanup timeout on component unmount
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [currentPage]);


  return (
    <div className="">
      <Helmet>
        <title>Available Plots for Sale | Your Site Name</title>
        <meta name="description" content="Browse our selection of premium plots for sale in prime locations. Find your perfect plot for residential or commercial development." />
      </Helmet>

      {/* Hero Section */}
      <div 
        className="breadcumb-wrapper" 
        style={{
          backgroundImage: "url('/assets/img/main/m1.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container">
          <div className="breadcumb-content">
            <h1 className="breadcumb-title">OUR PLOTS</h1>
            <ul className="breadcumb-menu">
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>Plots</li>
            </ul>
          </div>
        </div>
      </div>

      <section className="project-sec space-top space-extra-bottom">
        <div className="container">
          <div className="row align-items-center mb-4">
            <div className="col-lg-6">
              <div className="title-area">
                <span className="sub-title6">OUR PLOTS</span>
                <h2 className="sec-title">Available Plots</h2>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="search-area">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search plots..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  {loading && (
                    <span
                      className="position-absolute"
                      style={{
                        right: "60px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 4,
                      }}
                    >
                      <span className="spinner-border spinner-border-sm text-primary" />
                    </span>
                  )}
                  <button
                    className="btn btn-primary"
                    style={{
                      background: "var(--theme-color)",
                      border: "none",
                      padding: "0 25px",
                    }}
                    disabled={loading}
                  >
                    <i className="fas fa-search"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="row gy-4">
            {loading && searchTerm === "" ? (
              [...Array(6)].map((_, index) => <PlotSkeleton key={index} />)
            ) : filteredPlots.length === 0 ? (
              <NotFound message="No plots found." />
            ) : (
              filteredPlots.map((plot) => (
                <PlotCard key={plot.id} plot={plot} />
              ))
            )}
          </div>

          {!loading && totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                setCurrentPage(page);
                window.scrollTo(0, 0);
              }}
            />
          )}
        </div>
      </section>
    </div>
  );
};

export default Plots;
